import { Color, Spacing } from '@persona-id/design-tokens';
import { ChevronRightIcon } from '@persona-id/design-tokens/icons';
import { capitalize, get, last } from 'lodash/fp';
import { ComponentProps } from 'react';
import { components as ReactSelectComponents, OptionProps } from 'react-select';
import styled from 'styled-components';

import { formatDisplayValue, Option } from 'dashboard/components/JsonSchemaSelect/utils';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { getJsonSchemaType } from 'dashboard/lib/workflows';
import { Paragraph } from 'design-system/components/Text';
import { JsonSchemaCustomTypes } from 'lib/fields';
import { isJsonSchemaRef, JsonSchema, JsonSchemaTypes } from 'lib/json-schema';

export const ObjectKeySuffix = '.[object]';

const Container = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const TextContainer = styled.div`
  flex: 1;
`;

const ArrowContainer = styled.div`
  display: flex;
  width: ${Spacing[4]};
`;

export type SelectOptionType = OptionType & Partial<Pick<Option, 'property' | 'schema'>>;

const formatSchemaType = (
  schemaType: JsonSchemaCustomTypes | JsonSchemaTypes,
  schema?: JsonSchema,
) => {
  if (schemaType == null) {
    return 'Unknown';
  }
  if (
    schemaType === JsonSchemaTypes.Array &&
    schema &&
    'type' in schema &&
    schema.type === JsonSchemaTypes.Array &&
    'type' in schema.items &&
    schema.items.type != null
  ) {
    return `Array of ${capitalize(schema.items.type)}`;
  }
  return capitalize(schemaType);
};

const SelectOption = (
  props: OptionProps<SelectOptionType> & {
    selectProps: ComponentProps<typeof ReactSelect<string>>;
  },
) => {
  const id =
    props.selectProps.id != null
      ? `${props.selectProps.id}-option-${last(props.innerProps.id?.split('-'))}`
      : undefined;
  const exampleData = props.selectProps.exampleData;
  const schemaType = getJsonSchemaType(props.data.schema!);
  const typeText = `Type: ${formatSchemaType(schemaType, props.data.schema)}`;
  const selectionText =
    (exampleData && formatDisplayValue(get(props.data.value, exampleData))) ||
    (props.data.schema && 'description' in props.data.schema && props.data.schema?.description) ||
    typeText;
  const isObjectSelectOption = props.data.value.endsWith(ObjectKeySuffix);

  return (
    <ReactSelectComponents.Option {...props} innerProps={{ ...props.innerProps, id }}>
      <Container>
        <TextContainer>
          <Paragraph style={{ lineBreak: 'anywhere' }}>
            {isObjectSelectOption ? props.data.label : props.data.property}
          </Paragraph>
          <Paragraph size='sm'>{isObjectSelectOption ? typeText : selectionText}</Paragraph>
        </TextContainer>
        {(schemaType === JsonSchemaTypes.Object || isJsonSchemaRef(props.data.schema!)) &&
          !isObjectSelectOption && (
            <ArrowContainer>
              <ChevronRightIcon color={Color.CoreSlate800} />
            </ArrowContainer>
          )}
      </Container>
    </ReactSelectComponents.Option>
  );
};

export default SelectOption;
